import {
  type FC,
  type PropsWithChildren,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';

import classNames from 'classnames';

import { useFullscreen } from '@iconik-io/shared-lib/src/react/hooks/use_fullscreen';
import { Icon } from '@iconik-io/uad';
import { useBasePlayerContext } from '@iconik-io/unified-player-ui-react';

import { routesWithPlayer } from '../../constants/routes';
import { FullscreenElementRefContext } from '../../contexts/fullscreen_element_ref';
import { togglePanelsInFullscreenMode } from '../../ducks/panels/slice';
import { useDefaultFullscreenPanel } from '../../hooks/use_default_fullscreen_panel';
import { useLeafRouteParams } from '../../router/use_leaf_route_params';
import { useAppDispatch, useAppSelector } from '../../store/react_redux_typed';
import Resizable from '../../ui/resizable/resizable';
import Tabs from '../../ui/tabs/tabs';
import SmallPanelsModal from '../modals/small_panels/small_panels';
import Panel from '../panel/panel';
import PanelWidget from '../panel_widget/panel_widget';
import SidebarMenu, {
  type SidebarMenuItemType,
} from '../sidebar_menu/sidebar_menu';
import type { PanelItem } from './types';
import { panelPrefix } from './utils';

import styles from './panels.module.css';

function getPanelComponent<P>(panel: PanelItem<P>) {
  return <panel.component {...panel.props} />;
}

type Props = PropsWithChildren<{
  className?: string;
  sidebarMenuClassName?: string;
  wrapperClassName?: string;
  availablePanels: PanelItem[];
  openPanel: (panel: string) => void;
  closePanel: (panel: string) => void;
  smallPanelsOpen: boolean;
  toggleSmallPanels: (value: boolean) => void;
  panelsHidden?: boolean;
  showSideMenu?: boolean;
  isCollectionPage?: boolean;
  setPanelPristineById?: (panelId: string) => void;
  podWidth?: number;
}>;

const Panels: FC<Props> = ({
  className,
  sidebarMenuClassName,
  wrapperClassName,
  availablePanels,
  openPanel,
  closePanel,
  smallPanelsOpen,
  toggleSmallPanels,
  panelsHidden,
  showSideMenu,
  podWidth,
  children,
}) => {
  const [panelToScroll, setPanelToScroll] = useState<string | undefined>();
  const [lastOpenedPanel, setLastOpenedPanel] = useState<string | undefined>();
  const { store } = useBasePlayerContext();
  const fullscreenElementRef = useContext(FullscreenElementRefContext);
  const panelsState = useAppSelector((state) => state.panels);
  const dispatch = useAppDispatch();
  const { route } = useLeafRouteParams();
  const defaultFullscreenPanel = useDefaultFullscreenPanel(availablePanels);
  const isFullscreen = useFullscreen();

  const isRouteWithPlayer = routesWithPlayer.has(route);

  useEffect(() => {
    if (!isRouteWithPlayer || !isFullscreen) {
      return;
    }

    if (!lastOpenedPanel) {
      setLastOpenedPanel(defaultFullscreenPanel);
    } else {
      // Here we handle edge case when we navigate in fullscreen mode from
      // video asset to other type of asset, when video-specific panel
      // that was initially active is not available anymore
      const hasPreviouslyOpenedPanel = availablePanels.some(
        (panel) => panel.key === lastOpenedPanel,
      );
      if (!hasPreviouslyOpenedPanel) {
        // Here we have two situations when defaultFullscreenPanel was previously
        // opened and not, in the second case we need to open panel as well
        setLastOpenedPanel(defaultFullscreenPanel);
        if (!panelsState.openedPanels[defaultFullscreenPanel]) {
          openPanel(defaultFullscreenPanel);
        }
      }
    }
  }, [
    availablePanels,
    isRouteWithPlayer,
    lastOpenedPanel,
    defaultFullscreenPanel,
    openPanel,
    panelsState.openedPanels,
    isFullscreen,
  ]);

  // Here we are doing additional clean up after closing the panel
  // to avoid having closed panel as the last opened panel
  useEffect(() => {
    setLastOpenedPanel(defaultFullscreenPanel);
  }, [defaultFullscreenPanel]);

  // Reveal opened panel in the browser viewport
  useEffect(() => {
    if (!panelToScroll) {
      return;
    }
    const newOpenedPanel = availablePanels.find(
      (panel) => panel.key === panelToScroll,
    );
    if (newOpenedPanel?.active) {
      const newOpenedDOM = document.querySelector(
        `[data-scroll-into-id=${panelPrefix}${panelToScroll}]`,
      );
      if (newOpenedDOM) {
        newOpenedDOM.scrollIntoView({ behavior: 'smooth' });
      }
    }
    setPanelToScroll(undefined);
  }, [panelToScroll, availablePanels]);

  const handleClosePanel = (panelId: string) => {
    // if we are closing recently opening panel, then we need
    // to set default panel to avoid broken sidebar toggle in videoplayer
    if (panelId === lastOpenedPanel) {
      setLastOpenedPanel(defaultFullscreenPanel);
    }
    closePanel(panelId);
  };

  const handleSidebarMenuClick = (item: SidebarMenuItemType) => {
    if (item.active) {
      handleClosePanel(item.key);
    } else {
      // Widget in panels is upload popup, in this case we don't
      // need to scroll or set last opened panel to avoid bugs
      if (!item.widget) {
        setPanelToScroll(item.key);
        setLastOpenedPanel(item.key);
      }
      openPanel(item.key);
    }
  };

  const handleSmallPanelsClose = () => {
    toggleSmallPanels(!smallPanelsOpen);
  };

  const renderPanelWidget = (panel: PanelItem) => (
    <PanelWidget panelId={panel.key} key={panel.reactKey || panel.key}>
      {getPanelComponent(panel)}
    </PanelWidget>
  );

  const fullscreenTabs = useMemo(
    () =>
      availablePanels
        .filter((panel) => !panel.widget && !panel.disabled)
        .map((panel) => ({
          icon: <Icon name={panel.iconName} key={panel.key} />,
          key: panel.key,
          color: panel.color,
        })),
    [availablePanels],
  );

  const renderPanel = (panel: PanelItem) => (
    <Resizable
      data-scroll-into-id={`${panelPrefix}${panel.key}`}
      className={classNames(styles.panel_wrapper, {
        [styles.hidden]: panel.disabled || panelsHidden,
        [styles.is_panel_selected]: panel.key === lastOpenedPanel,
      })}
      key={panel.reactKey || panel.key}
      minWidth={isFullscreen ? 400 : panel.minSize || 350}
      maxWidth={800}
      defaultSize={isFullscreen ? 400 : panel.defaultSize}
      side={!panel.side || panel.side === 'left' ? 'right' : 'left'}
      handleLabel={`Resize ${panel.title} panel`}
    >
      <div className={styles.panel_wrapper_inner}>
        <div className={styles.small_tabs}>
          <div className={styles.small_tabs_without_close}>
            {fullscreenTabs.map((item, index) => (
              <button
                key={item.key}
                className={styles.small_tab_button}
                style={{
                  color:
                    item.key === lastOpenedPanel
                      ? item.color
                      : 'var(--color-emphasis-medium-emphasis-content)',
                }}
                onClick={() => {
                  openPanel(item.key);
                  setLastOpenedPanel(item.key);
                }}
                type="button"
                role="tab"
                id={`tab-${index}`}
                aria-controls={`content-${index}`}
                aria-selected={false}
              >
                {item.icon}
              </button>
            ))}
          </div>

          <button
            className={classNames(
              styles.small_tab_button,
              styles.small_tab_close,
            )}
            onClick={() => {
              dispatch(togglePanelsInFullscreenMode());
              store.fullscreen.toggleFullscreenSidebarVisibility();
            }}
          >
            <Icon name="close" />
          </button>
        </div>

        <Panel
          className={classNames(panel.className, styles.panel_content)}
          title={panel.title}
          panelId={panel.key}
          color={panel.color}
          actions={panel.actions}
          side={panel.side ?? 'left'}
          helpButton={!isFullscreen ? panel.helpButton : undefined}
          canClose={!isFullscreen}
          canMaximize={panel.canMaximize}
          onClose={handleClosePanel}
        >
          {getPanelComponent(panel)}
        </Panel>
      </div>
    </Resizable>
  );

  const renderPanelItem = (panel: PanelItem) =>
    panel.widget ? renderPanelWidget(panel) : renderPanel(panel);

  const leftPanels = availablePanels.filter(
    (panel) => panel.active && (!panel.side || panel.side === 'left'),
  );
  const rightPanels = availablePanels.filter(
    (panel) => panel.active && panel.side === 'right',
  );
  const sidebarMenuItems = availablePanels.map(
    (panel): SidebarMenuItemType => ({
      active: panel.active,
      iconName: panel.iconName,
      color: panel.color,
      title: panel.title,
      key: panel.key,
      badge: panel.badge,
      badgeColor: panel.badgeColor,
      badgeLabel: panel.badgeLabel,
      disabled: panel.disabled,
      disabledTooltipText: panel.disabledTooltipText,
      bottom: panel.bottom,
      widget: panel.widget,
    }),
  );

  const tabs = availablePanels.map((panel) => ({
    title: <Icon name={panel.iconName} key={panel.key} />,
    content: getPanelComponent(panel),
    panelKey: panel.key,
    disabled: panel.disabled,
  }));

  return (
    <div className={classNames(className, styles.root)}>
      {showSideMenu !== false && sidebarMenuItems.length > 0 && (
        <SidebarMenu
          className={classNames(sidebarMenuClassName, styles.sidebar_menu, {
            [styles.hidden]: panelsHidden,
          })}
          onItemClick={handleSidebarMenuClick}
          items={sidebarMenuItems}
        />
      )}

      {/*
        On admin pages with panels we render Panels the second time under
        existing Panels component. In this case we need to avoid setting
        reference the second time, otherwise panels in fullscreen mode will
        be broken. We could avoid it by refactoring application having only
        one instance of Panels, but at the moment it is a too big task
      */}
      <div
        key="wrapper"
        className={classNames(wrapperClassName, styles.player_panels_wrapper)}
        ref={isRouteWithPlayer ? fullscreenElementRef : null}
      >
        {leftPanels.map(renderPanelItem)}

        <div
          key="content"
          style={{
            flex: `1 1 ${podWidth ? `${podWidth}px` : 'auto'}`,
          }}
          className={styles.content_wrapper}
        >
          {children}
        </div>

        <div
          className={classNames(styles.panels, {
            [styles.fullscreen_mode]: isFullscreen,
            [styles.is_panels_visible_in_fullscreen]:
              panelsState.isPanelsVisibleInFullscreen,
          })}
        >
          <div className={styles.panels_inner}>
            {rightPanels.map(renderPanelItem)}
          </div>
        </div>
      </div>

      {tabs.length > 0 && (
        <SmallPanelsModal
          isOpen={smallPanelsOpen}
          onRequestClose={handleSmallPanelsClose}
        >
          <Tabs
            tabs={tabs}
            className={styles.small_mobile_tabs}
            tabsClassName={styles.small_mobile_tab_buttons}
            onTabChange={(index) => {
              // TODO: To be able to change video position on comment interaction
              // in mobile view we are opening underlying panels when user clicks
              // tabs. But as a proper solution we need to be able to show the same
              // real panels in mobile view. It will solve the problem when user are
              // losing state in panels by rotating iPad from landscape to portrait mode.
              // https://linear.app/iconik/issue/FE-3094/keep-unsaved-changes-in-comments-when-switching-between-mobileregular
              if (isRouteWithPlayer) {
                openPanel(tabs[index].panelKey);
              }
            }}
          />
        </SmallPanelsModal>
      )}
    </div>
  );
};

export default Panels;
